/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Rent cheque status — PUT /re/rent-schedule/{id}
 * {status, cheque_no, bank_name, deposited_date, cleared_date, bounce_reason}.
 * Also exports the quick-action menu used in the cheque tables.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { DateField, KV } from './Widgets';
import {
  CHEQUE_STATUSES, errorMessage, formatDate, formatMoney, humanize, todayIso, useReApi,
} from '../api';
import { isAustralia } from '../../variant';

/** Next sensible statuses for a cheque (for quick buttons). */
export const nextChequeActions = (status) => ({
  PENDING: ['DEPOSITED', 'BOUNCED'],
  DEPOSITED: ['CLEARED', 'BOUNCED'],
  BOUNCED: ['REPLACED', 'DEPOSITED'],
}[status] || []);

export default function ChequeDialog({ cheque, targetStatus, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cheque) return;
    const status = targetStatus || cheque.status;
    setForm({
      status,
      cheque_no: cheque.cheque_no || '',
      bank_name: cheque.bank_name || '',
      deposited_date: cheque.deposited_date || (status === 'DEPOSITED' ? todayIso() : ''),
      cleared_date: cheque.cleared_date || (status === 'CLEARED' ? todayIso() : ''),
      bounce_reason: cheque.bounce_reason || '',
    });
    setErr(null);
  }, [cheque, targetStatus]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  const submit = async () => {
    if (form.status === 'BOUNCED' && !form.bounce_reason.trim()) {
      setErr('Enter the bounce reason (e.g. insufficient funds, signature mismatch).');
      return;
    }
    if (form.status === 'CLEARED' && !form.cleared_date) {
      setErr('Enter the cleared date.');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/re/rent-schedule/${cheque.id}`, {
        status: form.status,
        cheque_no: form.cheque_no || null,
        bank_name: form.bank_name || null,
        deposited_date: form.deposited_date || null,
        cleared_date: form.cleared_date || null,
        bounce_reason: form.status === 'BOUNCED' ? form.bounce_reason : (form.bounce_reason || null),
      });
      onDone(form.status);
    } catch (e) {
      setErr(errorMessage(e, 'Could not update the cheque'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(cheque)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{`${isAustralia() ? 'Payment' : 'Cheque'} ${cheque?.sequence ?? ''}${cheque?.lease_number ? ` · ${cheque.lease_number}` : ''}`}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <MDBox mb={2}>
          <KV label="Due" value={formatDate(cheque?.due_date)} />
          <KV label="Amount" value={formatMoney(cheque?.amount, cheque?.currency)} bold />
        </MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Status" value={form.status || 'PENDING'} onChange={set('status')}>
              {CHEQUE_STATUSES.map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
            </TextField>
          </Grid>
          {!isAustralia() && (
            <>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Cheque no." value={form.cheque_no || ''} onChange={set('cheque_no')} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Bank" value={form.bank_name || ''} onChange={set('bank_name')} /></Grid>
            </>
          )}
          <Grid item xs={12} sm={6}><DateField label="Deposited" value={form.deposited_date} onChange={(v) => set('deposited_date')(v || '')} sx={{ width: '100%' }} /></Grid>
          <Grid item xs={12} sm={6}><DateField label="Cleared" value={form.cleared_date} onChange={(v) => set('cleared_date')(v || '')} sx={{ width: '100%' }} /></Grid>
          {form.status === 'BOUNCED' && (
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={isAustralia() ? 'Why it was dishonoured' : 'Bounce reason'} value={form.bounce_reason || ''} onChange={set('bounce_reason')} required />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color={form.status === 'BOUNCED' ? 'error' : 'info'} onClick={submit} disabled={saving}>Save</MDButton>
      </DialogActions>
    </Dialog>
  );
}

ChequeDialog.propTypes = {
  cheque: PropTypes.object,
  targetStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};
ChequeDialog.defaultProps = { cheque: null, targetStatus: null };
